import React, { useState, useEffect, useCallback } from "react";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Input } from "../../../shared/components/Input.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { createDefaultShippingAddress, INDIAN_STATES } from "../models/customer.js";

export function ShippingAddressModal({
  isOpen,
  onClose,
  customer,
  onFetchAddresses,
  onAddAddress,
  onRemoveAddress
}) {
  const { confirmDanger } = useDangerConfirm();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [formData, setFormData] = useState(createDefaultShippingAddress());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadAddresses = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const data = await onFetchAddresses(customer.id);
      setAddresses(data);
    } catch (err) {
      console.error("Failed to load addresses:", err);
    } finally {
      setLoading(false);
    }
  }, [customer?.id, onFetchAddresses]);

  useEffect(() => {
    if (isOpen && customer) {
      setIsAddingNew(false);
      setFormData(createDefaultShippingAddress());
      setError("");
      loadAddresses();
    }
  }, [isOpen, customer, loadAddresses]);

  if (!customer) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStateChange = (e) => {
    const code = e.target.value;
    const match = INDIAN_STATES.find((s) => s.code === code) || INDIAN_STATES[0];
    setFormData((prev) => ({
      ...prev,
      stateCode: match.code,
      state: match.name
    }));
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!formData.addressLine1.trim() || !formData.city.trim()) {
      setError("Please fill in Address Line 1 and City.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onAddAddress(customer.id, formData);
      setIsAddingNew(false);
      setFormData(createDefaultShippingAddress());
      await loadAddresses();
    } catch (err) {
      setError(err.message || "Failed to save shipping address.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (await confirmDanger({ title: "Delete address?", message: "Delete this shipping destination?" })) {
      await onRemoveAddress(customer.id, addressId);
      await loadAddresses();
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`Shipping Addresses: ${customer.name}`}
    >
      <div>
        {!isAddingNew ? (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
                {addresses.length} saved destination{addresses.length === 1 ? "" : "s"}
              </div>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setIsAddingNew(true)}
              >
                + Add Destination
              </Button>
            </div>

            {loading ? (
              <div className="state-box">Loading addresses...</div>
            ) : addresses.length === 0 ? (
              <div className="state-box" style={{ padding: "1.5rem" }}>
                <div style={{ fontSize: "1.75rem", marginBottom: "0.5rem" }}>📍</div>
                <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>No shipping addresses added</div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", marginBottom: "1rem" }}>
                  By default, invoices will ship to the customer's billing address.
                </div>
                <Button variant="secondary" size="sm" onClick={() => setIsAddingNew(true)}>
                  Add First Shipping Location
                </Button>
              </div>
            ) : (
              <div>
                {addresses.map((addr) => (
                  <Card key={addr.id} style={{ marginBottom: "0.65rem", padding: "0.85rem 1rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ fontWeight: "600", fontSize: "0.95rem" }}>
                            {addr.label || "Warehouse / Site"}
                          </span>
                          {addr.isDefault && <Badge variant="success">Default</Badge>}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "3px" }}>
                          {addr.addressLine1} {addr.addressLine2 ? `, ${addr.addressLine2}` : ""}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                          {addr.city}, {addr.state} - {addr.pincode}
                        </div>
                        {addr.contactPerson && (
                          <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "3px" }}>
                            👤 {addr.contactPerson} {addr.contactMobile ? `(${addr.contactMobile})` : ""}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleDelete(addr.id)}
                        style={{ color: "var(--danger)" }}
                        aria-label="Delete address"
                      >
                        🗑️
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* New Address Sub-form */
          <form onSubmit={handleSaveAddress}>
            {error && (
              <div className="form-error" style={{ marginBottom: "1rem" }}>
                {error}
              </div>
            )}

            <Input
              label="Location / Tag Label"
              id="addr-label"
              name="label"
              value={formData.label}
              onChange={handleChange}
              placeholder="e.g. Mill Unit 2 / Hub Warehouse"
              required
            />

            <Input
              label="Address Line 1"
              id="addr-line1"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
              placeholder="Plot No. 12, Industrial Estate"
              required
            />

            <Input
              label="Address Line 2 (Optional)"
              id="addr-line2"
              name="addressLine2"
              value={formData.addressLine2}
              onChange={handleChange}
              placeholder="Near Railway Feeder Road"
            />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <Input
                label="City / Town"
                id="addr-city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Avinashi"
                required
              />

              <div className="form-group">
                <label htmlFor="addr-state" className="form-label">State</label>
                <select
                  id="addr-state"
                  value={formData.stateCode}
                  onChange={handleStateChange}
                  className="form-control"
                >
                  {INDIAN_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <Input
                label="Pincode"
                id="addr-pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="641654"
              />

              <Input
                label="Site Contact Person"
                id="addr-person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                placeholder="Mr. Sundar (Supervisor)"
              />
            </div>

            <Input
              label="Contact Phone"
              id="addr-mobile"
              name="contactMobile"
              type="tel"
              value={formData.contactMobile}
              onChange={handleChange}
              placeholder="+91 94433 11223"
            />

            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
              <Button
                type="button"
                variant="secondary"
                block
                onClick={() => setIsAddingNew(false)}
              >
                Back to List
              </Button>
              <Button
                type="submit"
                variant="primary"
                block
                loading={submitting}
              >
                Save Destination
              </Button>
            </div>
          </form>
        )}
      </div>
    </BottomSheet>
  );
}

export default ShippingAddressModal;
